import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, FlexboxGrid, Grid, Row, Text, Button } from 'rsuite';
import { useSelector } from 'react-redux';
import {
  selectConfigExpressRegistrationTypeVerbiage,
  selectConfigExpressRegistrationVerbiage,
  selectConfigExpressRegistrationRenewEnabled,
} from '../store/slices/config/configSelectors';
import { selectGlobalTranslation } from '../store/slices/globals/globalsSelectors';
import pluralize from 'pluralize';
import dayjs from 'dayjs';
import _ from 'lodash';
import PortalHeader from '../components/layout/PortalHeader';
import PermitFilters from '../components/allPermits/PermitFilters';
import PermitList from '../components/allPermits/PermitList';
import Notice from '../components/user-feedback/Notice';
import {
  C_ACTIVE_PERMIT_STATUSES,
  C_DEFAULT_TRANSLATIONS,
} from '../common/consts';
import {
  ALL_PERMITS_STATUS_OPTIONS_ENUM,
  PERMIT_SORT_OPTIONS_ENUM,
  LICENCES_SORT_CONFIG,
  LICENSES_STATUS_OPTIONS,
  LICENSES_SORT_OPTIONS,
} from '../common/enums';
import usePlaceNavigate from '../hooks/usePlaceNavigate';
import { getAddressWithUnitNumber, titleize } from '../common/utils';
import {
  selectUserLicenses,
  selectRenewableLicenses,
} from '../store/slices/userLicenses/userLicensesSelectors';
import {
  getUserLicensesAsync,
  getUsersRenewableLicensesAsync,
} from '../store/slices/userLicenses/userLicensesThunks';
import { resetRenewState } from '../store/slices/renew/renewSlice';
import { License, LooseLicense, Owner } from 'src/types/licenses';
import { useAppDispatch } from 'src/store/store';

const getNameOfOwner = ({ name, firstName, lastName }: Partial<Owner>) =>
  name || `${firstName} ${lastName}`;

const parseOwnerDetails = (ownerDetails?: Owner | Array<Owner>) => {
  if (!ownerDetails) return { owners: '', tooltip: '', allOwnersArray: [] };
  const ownersArray = Array.isArray(ownerDetails)
    ? ownerDetails.map(getNameOfOwner)
    : [getNameOfOwner(ownerDetails)];
  const owners =
    ownersArray.length > 1
      ? `${ownersArray[0]} (+${ownersArray.length - 1})`
      : ownersArray[0];
  const tooltip = ownersArray.length > 1 ? ownersArray.join(', ') : '';

  return { owners, tooltip, allOwnersArray: ownersArray };
};

const AllPermits = () => {
  const placeNavigate = usePlaceNavigate();
  const dispatch = useAppDispatch();

  const registrationVerbiage = useSelector(
    selectConfigExpressRegistrationVerbiage
  );
  const registrationTypeVerbiage = useSelector(
    selectConfigExpressRegistrationTypeVerbiage
  );
  const renewEnabled = useSelector(selectConfigExpressRegistrationRenewEnabled);
  const ownerVerbiage = useSelector(selectGlobalTranslation);

  const [searchFilter, setSearchFilter] = useState('');
  const [sortFilter, setSortFilter] = useState(
    PERMIT_SORT_OPTIONS_ENUM.ADDRESS_ASC
  );
  const [statusFilter, setStatusFilter] = useState(
    ALL_PERMITS_STATUS_OPTIONS_ENUM.ACTIVE
  );

  const {
    data: renewableLicenses,
    loading: renewableLicensesLoading,
    error: renewableLicensesError,
  } = useSelector(selectRenewableLicenses);
  const {
    data: userLicenses,
    loading: userLicensesLoading,
    error: userLicensesError,
  } = useSelector(selectUserLicenses);

  const loadLicensesData = useCallback(() => {
    dispatch(getUserLicensesAsync());
    if (renewEnabled) {
      dispatch(getUsersRenewableLicensesAsync());
    }
  }, [dispatch, renewEnabled]);

  useEffect(() => {
    loadLicensesData();
  }, [loadLicensesData]);

  const loading = useMemo(
    () => renewableLicensesLoading || userLicensesLoading,
    [renewableLicensesLoading, userLicensesLoading]
  );

  const errorMessage = useMemo(() => {
    return renewableLicensesError || userLicensesError;
  }, [renewableLicensesError, userLicensesError]);

  const licenses = useMemo((): LooseLicense[] => {
    return userLicenses?.map((license: License) => {
      const { owners, allOwnersArray, tooltip } = parseOwnerDetails(
        license.owner
      );
      const canRenew =
        renewEnabled &&
        license.license &&
        renewableLicenses.find(
          (renewLicense: License) => renewLicense.license === license.license
        );

      let displayExpirationDate = null;
      if (license.expiration_date) {
        const isExpirationDateValid = dayjs(license.expiration_date).isValid();
        displayExpirationDate = isExpirationDateValid
          ? dayjs(license.expiration_date).format('YYYY-MM-DD')
          : titleize(license.expiration_date);
      }

      return {
        address: getAddressWithUnitNumber(
          license.property_address,
          license.unit_number
        ),
        licenseNumber: license.license,
        applicationNumber: license.application_number,
        status: license.status,
        expirationDate: displayExpirationDate,
        ownersNames: owners,
        ownersNameToolTip: tooltip,
        allOwnersArray,
        canRenew: !!canRenew,
      };
    });
  }, [userLicenses, renewableLicenses, renewEnabled]);

  useEffect(() => {
    dispatch(resetRenewState());
  }, [dispatch]);

  const ownerLabel = useMemo(
    () => ownerVerbiage ?? C_DEFAULT_TRANSLATIONS.owner,
    [ownerVerbiage]
  );

  const statusOptions = useMemo(
    () => LICENSES_STATUS_OPTIONS(registrationTypeVerbiage),
    [registrationTypeVerbiage]
  );

  const sortOptions = useMemo(
    () => LICENSES_SORT_OPTIONS(registrationTypeVerbiage, ownerLabel),
    [ownerLabel, registrationTypeVerbiage]
  );

  const matchesSearchFilter = useCallback(
    (license: LooseLicense) => {
      if (searchFilter === '') return true; // Handle empty search filter

      const lowercasedFilter = searchFilter.toLowerCase();
      return (
        license.address.toLowerCase().includes(lowercasedFilter) ||
        license.licenseNumber?.toLowerCase().includes(lowercasedFilter) ||
        license.allOwnersArray?.some((owner) =>
          owner.toLowerCase().includes(lowercasedFilter)
        )
      );
    },
    [searchFilter]
  );

  const matchesStatusFilter = useCallback(
    (license: LooseLicense) => {
      if (statusFilter === ALL_PERMITS_STATUS_OPTIONS_ENUM.ALL) {
        return true;
      }
      if (statusFilter === ALL_PERMITS_STATUS_OPTIONS_ENUM.ACTIVE) {
        return C_ACTIVE_PERMIT_STATUSES.includes(license.status);
      }
      if (statusFilter === ALL_PERMITS_STATUS_OPTIONS_ENUM.INACTIVE) {
        return !C_ACTIVE_PERMIT_STATUSES.includes(license.status);
      }
      return false;
    },
    [statusFilter]
  );

  const filteredLicenses = useMemo((): LooseLicense[] => {
    const filtered = licenses?.filter(
      (license: LooseLicense) =>
        matchesSearchFilter(license) && matchesStatusFilter(license)
    );

    return filtered;
  }, [licenses, matchesStatusFilter, matchesSearchFilter]);

  const sortedLicenses = useMemo(() => {
    const { key, order } = LICENCES_SORT_CONFIG[sortFilter];

    const sortedArray = _.sortBy(filteredLicenses, (item: LooseLicense) => {
      const value = item[key as keyof LooseLicense];
      return value === null || value === undefined ? '' : value;
    });

    if (order === 'desc') {
      return sortedArray.reverse();
    }

    return sortedArray;
  }, [filteredLicenses, sortFilter]);

  return (
    <FlexboxGrid justify="space-between">
      <FlexboxGrid.Item colspan={24}>
        <PortalHeader />
      </FlexboxGrid.Item>

      <FlexboxGrid.Item
        as="main"
        id="main-content"
        tabIndex={-1}
        className="mt-24 pb-24 ph-48"
        colspan={24}
        aria-label={`My ${pluralize(registrationVerbiage ?? '')}`}
      >
        <Grid fluid>
          <Row className="mb-24">
            <Col xs={24}>
              <Button
                appearance="subtle"
                className="link-gray"
                onClick={() => {
                  placeNavigate('/dashboard');
                }}
              >
                &lt; Back
              </Button>
            </Col>
          </Row>
          <Row className="mb-20">
            <Col xs={24}>
              <Text
                as="h1"
                className="header-text-h3"
              >
                My {pluralize(registrationVerbiage ?? '')}
              </Text>
            </Col>
          </Row>

          {errorMessage && <Notice message={errorMessage} />}
          {!errorMessage && (
            <>
              <PermitFilters
                ownerLabel={ownerLabel}
                registrationTypeVerbiage={registrationTypeVerbiage}
                registrationVerbiage={registrationVerbiage ?? ''}
                searchFilter={searchFilter}
                setSearchFilter={setSearchFilter}
                setSortFilter={setSortFilter}
                setStatusFilter={setStatusFilter}
                sortFilter={sortFilter}
                sortOptions={sortOptions}
                statusFilter={statusFilter}
                statusOptions={statusOptions}
              />
              <PermitList
                licenses={sortedLicenses || []}
                registrationTypeVerbiage={registrationTypeVerbiage}
                ownerLabel={ownerLabel}
                loading={loading}
              />
            </>
          )}
        </Grid>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default AllPermits;
